/**
 * receiptView.ts — View Receipt on a completed deal, read from the server.
 *
 * WHAT WAS WRONG (card 1218933500630001). app.js's receipt pop-up painted only
 * from the thread's `fees`, and those load only while a deal is ACCEPTED
 * (floorTransport's mapThreadFees refuses any other status). View Receipt shows
 * only once a deal is COMPLETED. So the button appeared exactly when its data
 * could no longer load, and a tap painted nothing: no receipt, no error.
 * Reproduced signed in on staging 2026-10-01.
 *
 * WHY NOT LOAD `fees` FOR A COMPLETED DEAL. `fees` does double duty: truthy fees
 * are what make #/checkout/<id> resolvable (app.js, the checkout route's
 * `THREADS.find((x) => x.id === m[1] && x.fees)`). Filling them for a completed
 * deal would offer to charge again for a deal that is paid and done. So the
 * receipt lives in its own store, read from GET /api/receipts/:offerId (which
 * answers for accepted AND completed offers, to a party only), and nothing here
 * writes `fees`.
 *
 * WHICH OFFER. The list row's own offer id, through the bridge lookup the
 * dispute form uses (__gbzDisputeContext): rewritten by every list read, so it
 * is the offer that completed, not one a counter superseded.
 *
 * WHAT A PERSON SEES. Blake's pop-up, carrying the server's reference, with the
 * app's own receipt rules (artifacts/mobile/app/receipt/[id].tsx): the buyer
 * sees the payment breakdown and their card; the seller sees only their
 * proceeds, never the buyer's total or card. A refusal shows the server's own
 * sentence. A failure with no sentence shows the app's own "Receipt not
 * available".
 */

import { UNRESOLVED_OFFER_MESSAGE } from "./dealActions";

export const VIEW_RECEIPT = "[data-viewreceipt]";
export const receiptEndpoint = (offerId: string): string => `/api/receipts/${encodeURIComponent(offerId)}`;
/** The offer read, for the pieces the buyer asked for beyond the anchor (requestedPiecesOf). */
export const offerEndpoint = (offerId: string): string => `/api/offers/${encodeURIComponent(offerId)}`;
/** The app's own words when a receipt can't be read (receipt/[id].tsx). */
export const RECEIPT_UNAVAILABLE = "Receipt not available";
/** Reused, not new: dealActions' approved line for a deal whose offer id has not loaded. */
export { UNRESOLVED_OFFER_MESSAGE };

export interface ServerReceiptFees {
  itemAmountCents: number;
  platformFeeCents: number;
  coverageFeeCents: number;
  shippingCostCents: number;
  taxAmountCents: number;
  totalCharged: number;
  cardLast4: string | null;
  cardBrand: string | null;
  escrowReleased: boolean;
}

/** What the pop-up paints, out of receipts.ts's response. */
export interface ServerReceipt {
  refNum: string;
  offerType: string;
  cashAmount: number | null;
  itemValue: number | null;
  sellerCashProceeds: number;
  status: string;
  isBuyer: boolean;
  fees: ServerReceiptFees | null;
  /** The seller's pieces beyond the anchor, from the offer read; absent until it answers. */
  requestedItems?: ReceiptPiece[];
}

export interface ReceiptPiece { title: string; image: string | null }

const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);
const numOrNull = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
const strOrNull = (v: unknown): string | null => (typeof v === "string" && v ? v : null);

/**
 * Keep only what the pop-up paints. A body without the server's reference or
 * the buyer/seller flag is refused rather than guessed at, because the flag
 * decides whether a card and a total are shown.
 */
export function toServerReceipt(raw: unknown): ServerReceipt | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.refNum !== "string" || !r.refNum || typeof r.isBuyer !== "boolean") return null;
  const f = r.fees && typeof r.fees === "object" ? (r.fees as Record<string, unknown>) : null;
  /* The buyer's own figures. A seller's copy keeps none of them, so nothing the
     server might send can reach a seller's screen: their rows are the item
     price, the shipping the buyer paid, and their proceeds. */
  const buyerOnly = (v: unknown): number => (r.isBuyer ? num(v) : 0);
  return {
    refNum: r.refNum,
    offerType: typeof r.offerType === "string" ? r.offerType : "",
    cashAmount: numOrNull(r.cashAmount),
    itemValue: numOrNull(r.itemValue),
    sellerCashProceeds: num(r.sellerCashProceeds),
    status: typeof r.status === "string" ? r.status : "",
    isBuyer: r.isBuyer,
    fees: f
      ? {
          itemAmountCents: num(f.itemAmountCents),
          platformFeeCents: buyerOnly(f.platformFeeCents),
          coverageFeeCents: buyerOnly(f.coverageFeeCents),
          shippingCostCents: num(f.shippingCostCents),
          taxAmountCents: buyerOnly(f.taxAmountCents),
          totalCharged: buyerOnly(f.totalCharged),
          /* The server sends the card to the buyer only; the seller's copy drops
             it here too. */
          cardLast4: r.isBuyer ? strOrNull(f.cardLast4) : null,
          cardBrand: r.isBuyer ? strOrNull(f.cardBrand) : null,
          escrowReleased: f.escrowReleased === true,
        }
      : null,
  };
}

/**
 * The pieces the buyer asked for beyond the anchor, out of GET /api/offers/:id (card
 * 1219708926884343, IP2 review A1). The list row carries the offered side only
 * (lib/offeredItems.ts), so View Receipt listed the anchor alone while the server traded
 * every piece; the offer read carries both sides, in the server's position order. Anything
 * unreadable gives no pieces, which is today's receipt.
 */
export function requestedPiecesOf(raw: unknown): ReceiptPiece[] {
  if (!raw || typeof raw !== "object") return [];
  const r = raw as { offer?: { listingId?: unknown }; requestedItems?: unknown };
  if (!Array.isArray(r.requestedItems)) return [];
  const anchor = r.offer && typeof r.offer.listingId === "string" ? r.offer.listingId : null;
  return r.requestedItems.flatMap((it: unknown): ReceiptPiece[] => {
    if (!it || typeof it !== "object") return [];
    const p = it as { name?: unknown; image?: unknown; listingId?: unknown };
    if (typeof p.name !== "string") return [];
    /* The anchor is already on the receipt (the row's own listing): never twice. */
    if (anchor && p.listingId === anchor) return [];
    return [{ title: p.name, image: strOrNull(p.image) }];
  });
}

type ReceiptWindow = Window & {
  toast?: (message: string) => void;
  rerenderKeepScroll?: () => void;
  __gbzDisputeContext?: (threadId: string) => { offerId: string | null } | null;
  __gbzReceiptFor?: (threadId: string) => ServerReceipt | null;
};

export function installReceiptView(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  {
    notify = (message: string) => {
      if (!win.document.getElementById("toast")) return;
      (win as ReceiptWindow).toast?.(message);
    },
  }: { notify?: (m: string) => void } = {},
): () => void {
  const w = win as ReceiptWindow;
  /* threadId -> the server's receipt, with the offer it was read for. Its own
     store: never t.fees (see above). */
  const store = new Map<string, { offerId: string; receipt: ServerReceipt }>();
  /* One read per deal at a time. */
  const busy = new Set<string>();
  const offerFor = (threadId: string): string | null => w.__gbzDisputeContext?.(threadId)?.offerId ?? null;

  /* Only a receipt for the deal's CURRENT offer: the thread id is
     counterpart + listing, so a newer offer on the same pair and listing must
     not show the older one's reference and numbers. */
  w.__gbzReceiptFor = (threadId: string) => {
    const hit = store.get(threadId);
    return hit && hit.offerId === offerFor(threadId) ? hit.receipt : null;
  };

  async function load(threadId: string): Promise<void> {
    /* After app.js's own handler has repainted: render() rebuilds #toast, so a
       message sent before that repaint would be wiped. A macrotask, because a
       real tap runs microtasks between the capture listener and app.js's. */
    await new Promise<void>((resolve) => win.setTimeout(resolve, 0));
    const startedOn = win.location.hash;
    const offerId = offerFor(threadId);
    if (!offerId) {
      notify(UNRESOLVED_OFFER_MESSAGE);
      return;
    }
    let res: Response;
    const receiptRead = authFetch(receiptEndpoint(offerId));
    /* The offer read rides the same tap, beside the receipt read, never the Deals list. A
       failure (or no answer) gives no pieces, so the receipt stays exactly as it is today. */
    const pieces = (async (): Promise<ReceiptPiece[]> => {
      const r = await authFetch(offerEndpoint(offerId));
      return r.ok ? requestedPiecesOf(await r.json()) : [];
    })().catch((): ReceiptPiece[] => []);
    try {
      res = await receiptRead;
    } catch {
      notify(RECEIPT_UNAVAILABLE);
      return;
    }
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }
    if (!res.ok) {
      const said = body && typeof (body as { error?: unknown }).error === "string" ? (body as { error: string }).error : "";
      notify(said || RECEIPT_UNAVAILABLE);
      return;
    }
    const receipt = toServerReceipt(body);
    if (!receipt) {
      notify(RECEIPT_UNAVAILABLE);
      return;
    }
    /* The pop-up is a site-wide overlay (app.js renderShellExtras), so a read
       that lands after the person left the page is dropped rather than popping
       a receipt over wherever they went. Tapping View Receipt again reads it. */
    if (win.location.hash !== startedOn) return;
    store.set(threadId, { offerId, receipt });
    w.rerenderKeepScroll?.();
    /* The pieces join the receipt when the offer read answers; the receipt above never waits on it. */
    void pieces.then((requestedItems) => {
      const hit = store.get(threadId);
      if (!requestedItems.length || !hit || hit.receipt !== receipt) return;
      store.set(threadId, { offerId, receipt: { ...receipt, requestedItems } });
      if (win.location.hash === startedOn) w.rerenderKeepScroll?.();
    });
  }

  /* Capture, never stopping propagation: app.js's own handler still sets
     receiptFor and repaints; this reads the receipt alongside it. */
  const onClick = (e: Event) => {
    const el = (e.target as Element | null)?.closest?.(VIEW_RECEIPT);
    if (!el) return;
    const threadId = el.getAttribute("data-viewreceipt");
    if (!threadId || busy.has(threadId)) return;
    busy.add(threadId);
    void load(threadId).finally(() => busy.delete(threadId));
  };

  win.document.addEventListener("click", onClick, true);
  return () => {
    win.document.removeEventListener("click", onClick, true);
    delete w.__gbzReceiptFor;
  };
}
